/**
 * Application String Constants
 * 
 * Centralized string definitions for UI labels, messages, validation errors, etc.
 * This makes it easier to update text and support internationalization in the future.
 * 
 * @example
 * // Instead of: <h1>Login</h1>
 * // Use: <h1>{{ STRINGS.AUTH.LOGIN_TITLE }}</h1>
 */

export const STRINGS = {
  // Application Info
  APP: {
    NAME: 'Car Loan Finance',
    VERSION: 'v1.0.0',
    TAGLINE: 'Manage Your Loans Easily',
    COPYRIGHT: '© 2025 Car Loan Finance. All rights reserved.'
  },
  
  // Common/Generic Strings
  COMMON: {
    // Actions
    OK: 'OK',
    CANCEL: 'Cancel',
    SUBMIT: 'Submit',
    SAVE: 'Save',
    DELETE: 'Delete',
    EDIT: 'Edit',
    UPDATE: 'Update',
    CREATE: 'Create',
    BACK: 'Back',
    NEXT: 'Next',
    PREVIOUS: 'Previous',
    CLOSE: 'Close',
    CONFIRM: 'Confirm',
    CONTINUE: 'Continue',
    RETRY: 'Retry',
    REFRESH: 'Refresh',
    SEARCH: 'Search',
    FILTER: 'Filter',
    CLEAR: 'Clear',
    APPLY: 'Apply',
    DOWNLOAD: 'Download',
    UPLOAD: 'Upload',
    VIEW: 'View',
    VIEW_DETAILS: 'View Details',
    EXPORT: 'Export',
    PRINT: 'Print',
    SHARE: 'Share',
    COPY: 'Copy',
    
    // Status
    LOADING: 'Loading...',
    PROCESSING: 'Processing...',
    SUCCESS: 'Success',
    ERROR: 'Error',
    WARNING: 'Warning',
    INFO: 'Information',
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    COMPLETED: 'Completed',
    IN_PROGRESS: 'In Progress',
    
    // Labels
    NAME: 'Name',
    EMAIL: 'Email',
    PASSWORD: 'Password',
    PHONE: 'Phone',
    ADDRESS: 'Address',
    DATE: 'Date',
    TIME: 'Time',
    AMOUNT: 'Amount',
    STATUS: 'Status',
    TYPE: 'Type',
    DESCRIPTION: 'Description',
    NOTES: 'Notes',
    
    // Messages
    NO_DATA: 'No data available',
    NO_RESULTS: 'No results found',
    COMING_SOON: 'Coming Soon',
    UNDER_MAINTENANCE: 'Under Maintenance',
    TRY_AGAIN_LATER: 'Please try again later'
  },
  
  // Authentication Strings
  AUTH: {
    // Titles
    LOGIN_TITLE: 'Log In Your Account',
    REGISTER_TITLE: 'Create New Account',
    FORGOT_PASSWORD_TITLE: 'Reset Password',
    RESET_PASSWORD_TITLE: 'Create New Password',
    
    // Labels
    EMAIL_LABEL: 'Email Address',
    PASSWORD_LABEL: 'Password',
    CONFIRM_PASSWORD_LABEL: 'Confirm Password',
    NEW_PASSWORD_LABEL: 'New Password',
    CURRENT_PASSWORD_LABEL: 'Current Password',
    REMEMBER_ME: 'Remember me',
    FORGOT_PASSWORD: 'Forgot Password?',
    FULL_NAME_LABEL: 'Full Name',
    ROLE_LABEL: 'Select Role',
    
    // Placeholders
    EMAIL_PLACEHOLDER: 'Enter your email',
    PASSWORD_PLACEHOLDER: 'Enter your password',
    CONFIRM_PASSWORD_PLACEHOLDER: 'Re-enter your password',
    NAME_PLACEHOLDER: 'Enter your full name',
    
    // Buttons
    LOGIN_BUTTON: 'Log In',
    REGISTER_BUTTON: 'Create Account',
    LOGOUT_BUTTON: 'Log Out',
    RESET_PASSWORD_BUTTON: 'Reset Password',
    SEND_RESET_LINK: 'Send Reset Link',
    BACK_TO_LOGIN: 'Back to Login',
    LOGIN_WITH_BIOMETRIC: 'Login with Fingerprint',
    
    // Messages
    LOGIN_SUCCESS: 'Login successful!',
    LOGOUT_SUCCESS: 'Logged out successfully',
    REGISTER_SUCCESS: 'Account created successfully! Please sign in.',
    PASSWORD_RESET_SENT: 'Password reset link sent to your email',
    PASSWORD_RESET_SUCCESS: 'Password reset successful',
    PASSWORD_CHANGED: 'Password changed successfully',
    
    // Errors
    LOGIN_FAILED: 'Login failed. Please check your credentials.',
    REGISTER_FAILED: 'Registration failed. Please try again.',
    INVALID_EMAIL: 'Please enter a valid email address',
    INVALID_PASSWORD: 'Password must be at least 6 characters',
    PASSWORDS_NOT_MATCH: 'Passwords do not match',
    EMAIL_ALREADY_EXISTS: 'Email already exists',
    WEAK_PASSWORD: 'Password is too weak',
    REQUIRED_FIELD: 'This field is required',
    INVALID_CREDENTIALS: 'Invalid email or password',
    ACCOUNT_LOCKED: 'Account has been locked. Contact support.',
    SESSION_EXPIRED: 'Your session has expired. Please login again.',
    
    // Additional Text
    NO_ACCOUNT: "Don't have an account?",
    HAVE_ACCOUNT: 'Already have an account?',
    SIGN_UP_NOW: 'Sign Up',
    SIGN_IN_NOW: 'Sign In',
    OR_LOGIN_WITH: 'Or Login with',
    AGREE_TO_TERMS: 'I agree to the',
    TERMS_AND_CONDITIONS: 'Terms and Conditions',
    PRIVACY_POLICY: 'Privacy Policy',
    RESET_PASSWORD_INSTRUCTIONS: 'Enter your email address and we\'ll send you a link to reset your password.',
    CHECK_EMAIL: 'Check your email for the reset link',
    
    // Roles
    ROLE_CUSTOMER: 'Customer',
    ROLE_ADMIN: 'Admin',
    ROLE_MANAGER: 'Manager',
    ROLE_COLLECTION_AGENT: 'Collection Agent'
  },
  
  // Dashboard Strings
  DASHBOARD: {
    TITLE: 'Dashboard',
    WELCOME: 'Welcome back',
    OVERVIEW: 'Overview',
    QUICK_ACTIONS: 'Quick Actions',
    RECENT_ACTIVITIES: 'Recent Activities',
    STATISTICS: 'Statistics',
    NO_ACTIVITIES: 'No recent activities'
  },
  
  // Loan Strings
  LOAN: {
    // Titles
    MY_LOANS: 'My Loans',
    LOAN_DETAILS: 'Loan Details',
    APPLY_LOAN: 'Apply for Loan',
    LOAN_APPLICATION: 'Loan Application',
    LOAN_CALCULATOR: 'Loan Calculator',
    
    // Labels
    LOAN_AMOUNT: 'Loan Amount',
    LOAN_TERM: 'Loan Term',
    INTEREST_RATE: 'Interest Rate',
    MONTHLY_PAYMENT: 'Monthly Payment',
    TOTAL_AMOUNT: 'Total Amount',
    LOAN_TYPE: 'Loan Type',
    LOAN_PURPOSE: 'Loan Purpose',
    LOAN_STATUS: 'Loan Status',
    OUTSTANDING_BALANCE: 'Outstanding Balance',
    PAID_AMOUNT: 'Paid Amount',
    DUE_DATE: 'Due Date',
    NEXT_PAYMENT: 'Next Payment',
    
    // Status
    PENDING_APPROVAL: 'Pending Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ACTIVE: 'Active',
    CLOSED: 'Closed',
    OVERDUE: 'Overdue',
    
    // Messages
    NO_LOANS: 'No loans found',
    LOAN_APPLIED_SUCCESS: 'Loan application submitted successfully',
    LOAN_APPROVED: 'Your loan has been approved',
    LOAN_REJECTED: 'Your loan application has been rejected',
    
    // Buttons
    APPLY_NOW: 'Apply Now',
    VIEW_LOAN: 'View Loan',
    MAKE_PAYMENT: 'Make Payment',
    DOWNLOAD_SCHEDULE: 'Download Schedule'
  },
  
  // Payment Strings
  PAYMENT: {
    // Titles
    PAYMENTS: 'Payments',
    PAYMENT_HISTORY: 'Payment History',
    MAKE_PAYMENT: 'Make Payment',
    UPCOMING_PAYMENTS: 'Upcoming Payments',
    
    // Labels
    PAYMENT_AMOUNT: 'Payment Amount',
    PAYMENT_DATE: 'Payment Date',
    PAYMENT_METHOD: 'Payment Method',
    PAYMENT_STATUS: 'Payment Status',
    TRANSACTION_ID: 'Transaction ID',
    PAYMENT_REFERENCE: 'Payment Reference',
    
    // Status
    PAID: 'Paid',
    UNPAID: 'Unpaid',
    OVERDUE: 'Overdue',
    PENDING: 'Pending',
    FAILED: 'Failed',
    
    // Messages
    PAYMENT_SUCCESS: 'Payment successful',
    PAYMENT_FAILED: 'Payment failed',
    NO_PAYMENTS: 'No payment history',
    NO_UPCOMING_PAYMENTS: 'No upcoming payments',
    
    // Methods
    CREDIT_CARD: 'Credit Card',
    DEBIT_CARD: 'Debit Card',
    BANK_TRANSFER: 'Bank Transfer',
    UPI: 'UPI',
    CASH: 'Cash'
  },
  
  // Profile Strings
  PROFILE: {
    TITLE: 'Profile',
    MY_PROFILE: 'My Profile',
    EDIT_PROFILE: 'Edit Profile',
    PERSONAL_INFO: 'Personal Information',
    CONTACT_INFO: 'Contact Information',
    CHANGE_PASSWORD: 'Change Password',
    UPDATE_SUCCESS: 'Profile updated successfully',
    UPDATE_FAILED: 'Failed to update profile'
  },
  
  // Settings Strings
  SETTINGS: {
    TITLE: 'Settings',
    GENERAL: 'General',
    NOTIFICATIONS: 'Notifications',
    SECURITY: 'Security',
    PRIVACY: 'Privacy',
    LANGUAGE: 'Language',
    THEME: 'Theme',
    ABOUT: 'About',
    HELP: 'Help & Support',
    LOGOUT: 'Logout',
    ENABLE_NOTIFICATIONS: 'Enable Notifications',
    ENABLE_BIOMETRIC: 'Enable Biometric Login',
    DARK_MODE: 'Dark Mode'
  },
  
  // Notification Strings
  NOTIFICATION: {
    TITLE: 'Notifications',
    NO_NOTIFICATIONS: 'No notifications',
    MARK_ALL_READ: 'Mark all as read',
    CLEAR_ALL: 'Clear all',
    NEW_NOTIFICATION: 'New notification'
  },
  
  // Error Messages
  ERROR: {
    TITLE: 'Error',
    GENERIC: 'Something went wrong. Please try again.',
    NETWORK: 'Network error. Please check your connection.',
    SERVER: 'Server error. Please try again later.',
    NOT_FOUND: 'Resource not found',
    UNAUTHORIZED: 'You are not authorized to access this resource',
    FORBIDDEN: 'Access forbidden',
    TIMEOUT: 'Request timeout. Please try again.',
    VALIDATION: 'Please fill all required fields correctly',
    FILE_TOO_LARGE: 'File size is too large',
    INVALID_FILE_TYPE: 'Invalid file type',
    UPLOAD_FAILED: 'File upload failed',
    ACCOUNT_LOCKED: 'Account temporarily locked due to too many failed attempts',
    TOO_MANY_ATTEMPTS: 'Too many failed login attempts. Account locked for 5 minutes.',
    ATTEMPTS_LEFT: 'attempt(s) remaining',
    UNKNOWN_ERROR: 'An unknown error occurred',
    RESET_FAILED: 'Failed to send reset email. Please try again.',
    LOGIN_FAILED: 'Login failed. Please check your credentials.'
  },
  
  // Success Messages
  SUCCESS: {
    TITLE: 'Success',
    SAVED: 'Saved successfully',
    UPDATED: 'Updated successfully',
    DELETED: 'Deleted successfully',
    SUBMITTED: 'Submitted successfully',
    UPLOADED: 'Uploaded successfully',
    SENT: 'Sent successfully',
    LOGIN_SUCCESS: 'Login successful!',
    RESET_EMAIL_SENT: 'Password reset email sent successfully.'
  },
  
  // Loading Messages
  LOADING: {
    LOGGING_IN: 'Logging in...',
    RESETTING_PASSWORD: 'Sending reset email...'
  },
  
  // Info Messages
  INFO: {
    TITLE: 'Information',
    FINGERPRINT_NOT_IMPLEMENTED: 'Fingerprint authentication will be implemented soon.'
  },
  
  // Confirmation Messages
  CONFIRM: {
    DELETE: 'Are you sure you want to delete this?',
    LOGOUT: 'Are you sure you want to logout?',
    CANCEL: 'Are you sure you want to cancel?',
    SUBMIT: 'Are you sure you want to submit?',
    DISCARD_CHANGES: 'Discard unsaved changes?'
  },
  
  // Validation Messages
  VALIDATION: {
    REQUIRED: 'This field is required',
    EMAIL: 'Please enter a valid email',
    PASSWORD_MIN: 'Password must be at least 6 characters',
    PASSWORD_MATCH: 'Passwords must match',
    PHONE: 'Please enter a valid phone number',
    NUMBER: 'Please enter a valid number',
    MIN_VALUE: (min: number) => `Value must be at least ${min}`,
    MAX_VALUE: (max: number) => `Value must not exceed ${max}`,
    MIN_LENGTH: (min: number) => `Must be at least ${min} characters`,
    MAX_LENGTH: (max: number) => `Must not exceed ${max} characters`
  }
} as const;
